import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  class: "card-header",
  role: "button",
  "aria-controls": "contentIdForA11y3"
}
const _hoisted_2 = { class: "card-header-title title-flex pr-0" }
const _hoisted_3 = {
  key: 0,
  class: "percent-complete"
}
const _hoisted_4 = { class: "card-header-icon" }
const _hoisted_5 = { class: "card-content outer-container" }
const _hoisted_6 = { class: "content" }

import { mdiMenuDown, mdiMenuUp } from "@mdi/js";
import { computed, PropType, ref } from "vue";

import { TASKS } from "@/constants/assets.constant";
import { useContentStore } from "@/stores/content.store";
import { useUsersStore } from "@/stores/users.store";

import { ContentStructureSection } from "@/models/content-structure.model";
import { ProductItem } from "@/models/product.model";
import { TaskStatus } from "@/models/task.model";
import {
  PendingTransition,
  SidebarTransitionState
} from "@/models/transition-state.model";
import { OrganizationUser } from "@/models/user.model";
import { WorkflowState } from "@/models/workflow.model";

import { useFeatureFlags } from "@/composables/feature-flags";
import { useProductFeaturesManager } from "@/composables/product-features-manager";

import { formatCourse } from "@/utils/formatTimestamp";
import ContentWorkflowService from "@/utils/product/content-workflow.service";
import {
  getDeadlineByWorkflowState,
  getTaskStatus
} from "@/utils/tasks-helpers";

import TransitionConfirmation from "@/components/right-sidebars/content-sidebar/content-workflow/TransitionConfirmation.vue";

import ContentWorkflowItem from "./ContentWorkflowItem.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'ContentWorkflow',
  props: {
  product: {
    required: true,
    type: Object as PropType<ProductItem>
  },
  section: {
    required: true,
    type: Object as PropType<ContentStructureSection>
  },
  workflowStates: {
    required: true,
    type: Array as PropType<WorkflowState[]>
  },
  currentState: {
    required: true,
    type: Object as PropType<WorkflowState>
  },
  workflowStateFromTitleId: {
    required: true,
    type: Number
  }
},
  setup(__props) {

const usersStore = useUsersStore();
const contentStore = useContentStore();

const { isWorkflowManagementFeatureEnabled } = useProductFeaturesManager();
const { ffShouldTaskNotificationsBeSent } = useFeatureFlags();

const props = __props;

const getUsersAssignedToOtherWorkflow = (
  workflowUsers: number[]
): OrganizationUser[] => {
  return usersStore.users
    ? usersStore.users.filter((userToCheck: OrganizationUser) => {
        return workflowUsers.some((assignedUserId: number) => {
          return assignedUserId === userToCheck.id;
        });
      })
    : [];
};
const formatTimestampCourse = (date: number): string | undefined => {
  return date ? formatCourse(date) : undefined;
};
const getDeadlineForOtherWorkflow = (
  wfState: WorkflowState
): string | undefined => {
  const taskDeadline: number | undefined = props.product
    ? getDeadlineByWorkflowState(props.section, wfState, props.product)
    : undefined;

  return taskDeadline ? formatTimestampCourse(taskDeadline) : "";
};
const isTransitionAvailableForThisUser = (
  transition: SidebarTransitionState
): boolean => {
  return (
    !!usersStore.currentUser &&
    ContentWorkflowService.isTransitionAvailableForThisUser(
      transition,
      props.workflowStates,
      props.currentState,
      usersStore.currentUser.id,
      usersStore.canUserManageWorkflows
    )
  );
};
const getAllAvailableTransitionsFromThisStatus = (
  stateDetails: {
    id: number;
    name: string;
    status: TaskStatus;
    rank: number;
  },
  assignedUsers: number[]
): PendingTransition[] => {
  // Tasks which are upcoming/complete can be moved to in progress
  // Tasks which are upcoming/inprogress can be moved to complete
  let nextAvailableStatuses: PendingTransition[] = [];

  if (
    stateDetails.status === TaskStatus.UPCOMING ||
    stateDetails.status === TaskStatus.UPCOMING_LATE ||
    stateDetails.status === TaskStatus.FINAL_STATE ||
    stateDetails.status === TaskStatus.COMPLETE
  ) {
    nextAvailableStatuses.push({
      name: stateDetails.name,
      status: TaskStatus.CURRENT,
      availableStatus: "Mark as In Progress",
      id: stateDetails.id,
      assignedUsers: assignedUsers,
      currentStatus: stateDetails.status,
      taskName: stateDetails.name
    });
  }

  if (
    stateDetails.status === TaskStatus.UPCOMING ||
    stateDetails.status === TaskStatus.UPCOMING_LATE ||
    stateDetails.status === TaskStatus.CURRENT ||
    stateDetails.status === TaskStatus.CURRENT_LATE
  ) {
    const nextRankedTask: WorkflowState | undefined = props.workflowStates.find(
      (wf: WorkflowState): boolean => wf.rank === stateDetails.rank + 1
    );

    nextAvailableStatuses.push({
      name: nextRankedTask ? nextRankedTask.name : "",
      status: TaskStatus.COMPLETE,
      availableStatus: "Mark as Complete",
      id: nextRankedTask ? nextRankedTask.id : undefined,
      assignedUsers: assignedUsers,
      currentStatus: stateDetails.status,
      taskName: stateDetails.name
    });
  }

  // tasks which are currently active can be moved to the previous state
  if (
    stateDetails.status === TaskStatus.CURRENT ||
    stateDetails.status === TaskStatus.CURRENT_LATE
  ) {
    if (stateDetails.rank !== props.workflowStates[0].rank) {
      const prevRankedTask: WorkflowState | undefined =
        props.workflowStates.find(
          (wf: WorkflowState): boolean => wf.rank === stateDetails.rank - 1
        );

      nextAvailableStatuses.push({
        name: prevRankedTask ? prevRankedTask.name : "",
        status: TaskStatus.UPCOMING,
        availableStatus: "Move to previous state",
        id: prevRankedTask ? prevRankedTask.id : undefined,
        assignedUsers: assignedUsers,
        currentStatus: stateDetails.status,
        taskName: stateDetails.name
      });
    }
  }

  return nextAvailableStatuses;
};
const allowedTransitions = computed<SidebarTransitionState[]>(() => {
  return props.workflowStates
    .filter((wfState: WorkflowState): boolean => !wfState.isFinalState)
    .map((wfState: WorkflowState): SidebarTransitionState => {
      const { id, name, rank, instruction } = wfState;
      const status: TaskStatus | undefined = props.product
        ? getTaskStatus(props.section, wfState, props.product)
        : undefined;
      if (id && name && status && rank) {
        let stateDetails: SidebarTransitionState = {
          id,
          name,
          rank,
          status,
          instruction,
          isTransitionAvailableForThisUser: false,
          assignedUsers: getUsersAssignedToOtherWorkflow(wfState.users),
          deadline: getDeadlineForOtherWorkflow(wfState),
          availableTransitions: getAllAvailableTransitionsFromThisStatus(
            {
              id,
              name,
              status,
              rank
            },
            wfState.users
          )
        };
        stateDetails.isTransitionAvailableForThisUser =
          isTransitionAvailableForThisUser(stateDetails);
        return stateDetails;
      }
      return {
        assignedUsers: [],
        availableTransitions: [],
        deadline: undefined,
        id: undefined,
        instruction: "",
        isTransitionAvailableForThisUser: false,
        name: undefined,
        rank: undefined,
        status: TaskStatus.COMPLETE
      };
    });
});

const isConfirmTransitionOpen = ref<boolean>(false);
const isTransitionSubmitting = ref<boolean>(false);
const pendingTransition = ref<PendingTransition | undefined>(undefined);
const transitionMessage = ref<string>("");
const requestTransitionConfirmation = (transition: PendingTransition): void => {
  pendingTransition.value = transition;
  isConfirmTransitionOpen.value = true;
};
const closeTransitionConfirmation = (): void => {
  isConfirmTransitionOpen.value = false;
  transitionMessage.value = "";
  pendingTransition.value = undefined;
};
const submittingStateTransition = ref<boolean>(false);
const submitTransition = (
  transition: PendingTransition,
  productId: number
): Promise<void> => {
  submittingStateTransition.value = true;
  return contentStore
    .submitStateTransition({
      shouldProductBeUpdated: !transitionMessage.value,
      productId: productId,
      stateId: transition.id as number,
      sectionId: props.section.id,
      users_to_notify:
        ffShouldTaskNotificationsBeSent &&
        isWorkflowManagementFeatureEnabled.value
          ? transition.assignedUsers
          : [],
      status: {
        statusBefore: (TASKS.STATUSES_DISPLAY_NAME as Record<string, string>)[
          transition.currentStatus
        ],
        statusAfter: (TASKS.STATUSES_DISPLAY_NAME as Record<string, string>)[
          transition.status
        ],
        taskName: transition.taskName
      }
    })
    .then((): void => {
      submittingStateTransition.value = false;
    });
};
const confirmTransition = (): Promise<void> | void => {
  isTransitionSubmitting.value = true;
  const productValue = props.product;
  const pendingTransitionValue = pendingTransition.value;
  if (productValue && pendingTransitionValue && pendingTransitionValue.id) {
    let promises = [];
    promises.push(submitTransition(pendingTransitionValue, productValue.id));
    if (transitionMessage.value) {
      promises.push(
        contentStore.submitMessage({
          productId: productValue.id,
          sectionId: props.section.id,
          message: transitionMessage.value
        })
      );
    }
    return Promise.all(promises).then((): void => {
      closeTransitionConfirmation();
      isTransitionSubmitting.value = false;
    });
  }
};

const workflowIsOpen = ref<boolean>(false);
const toggleWorkflow = (): void => {
  workflowIsOpen.value = !workflowIsOpen.value;
};

const pinnedState = computed<SidebarTransitionState | undefined>(() => {
  let stateInProgress = allowedTransitions.value.find(
    (transition: SidebarTransitionState): boolean =>
      transition.id === props.currentState.id
  );
  if (props.workflowStateFromTitleId) {
    return allowedTransitions.value.find(
      (transition: SidebarTransitionState): boolean =>
        transition.id === props.workflowStateFromTitleId
    );
  }
  return stateInProgress;
});
const percentageComplete = computed<number>(() => {
  const numComplete: number = allowedTransitions.value.filter(
    (wf: SidebarTransitionState) => {
      return wf.status === TaskStatus.COMPLETE;
    }
  ).length;
  const numTasksWhichAreActionable: number = allowedTransitions.value.filter(
    (wf: SidebarTransitionState) => {
      return wf.status !== TaskStatus.FINAL_STATE;
    }
  ).length;
  return Math.round((numComplete * 100) / numTasksWhichAreActionable);
});

return (_ctx: any,_cache: any) => {
  const _component_SVGIcon = _resolveComponent("SVGIcon")!
  const _component_b_collapse = _resolveComponent("b-collapse")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_component_b_collapse, {
      class: "card",
      animation: "slide",
      "aria-id": "contentIdForA11y3",
      "model-value": workflowIsOpen.value,
      onOpen: toggleWorkflow,
      onClose: toggleWorkflow,
      "data-testing": "workflow-collapse"
    }, {
      trigger: _withCtx((props) => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("p", _hoisted_2, [
            _createElementVNode("span", null, "Tasks in " + _toDisplayString(__props.section.name), 1),
            (percentageComplete.value > 0)
              ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(percentageComplete.value) + "% Complete", 1))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("a", _hoisted_4, [
            _createVNode(_component_SVGIcon, {
              path: props.open ? _unref(mdiMenuDown) : _unref(mdiMenuUp),
              size: 24
            }, null, 8, ["path"])
          ])
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allowedTransitions.value, (transition) => {
                return (_openBlock(), _createBlock(ContentWorkflowItem, {
                  key: transition.id,
                  transition: transition,
                  "current-state": __props.currentState,
                  "is-pinned-state": true,
                  "submitting-state-transition": submittingStateTransition.value,
                  onRequestTransitionConfirmation: requestTransitionConfirmation
                }, null, 8, ["transition", "current-state", "submitting-state-transition"]))
              }), 128))
            ])
          ])
        ])
      ]),
      _: 1
    }, 8, ["model-value"]),
    (!workflowIsOpen.value && pinnedState.value)
      ? (_openBlock(), _createBlock(ContentWorkflowItem, {
          key: 0,
          transition: pinnedState.value,
          "current-state": __props.currentState,
          "is-pinned-state": true,
          "submitting-state-transition": submittingStateTransition.value,
          onRequestTransitionConfirmation: requestTransitionConfirmation
        }, null, 8, ["transition", "current-state", "submitting-state-transition"]))
      : _createCommentVNode("", true),
    (pendingTransition.value)
      ? (_openBlock(), _createBlock(TransitionConfirmation, {
          key: 1,
          modelValue: transitionMessage.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((transitionMessage).value = $event)),
          "pending-transition": pendingTransition.value,
          section: __props.section,
          "is-transition-submitting": isTransitionSubmitting.value,
          onCloseTransitionConfirmation: closeTransitionConfirmation,
          onConfirmTransition: confirmTransition
        }, null, 8, ["modelValue", "pending-transition", "section", "is-transition-submitting"]))
      : _createCommentVNode("", true)
  ]))
}
}

})